import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import Dialog from "@/components/Dialog";
import Radio from "@/components/Radio";
import SubSectionHeading from "@/components/SubSectionHeading";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { copyTextToClipboard } from "@/lib/copyToClipboard";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { COMMITTEE_ASSIGNMENT_TITLES_QUERY } from "../queries";
import type { CommitteeAssignmentTitle } from "../types";
import {
  assignmentRoleFilter,
  buildCommitteeMailtoHref,
  collectEmailsFromAssignmentNodes,
  fetchCommitteeParticipantEmails,
  formatEmailAddressList,
  isMailtoTooLong,
  openMailtoHref,
  type CommitteeParticipantEmailNode,
  type MailtoField
} from "./committeeMailto";

const EMPTY_EMAIL_RESULT = {
  emails: [] as string[],
  participantCount: 0,
  excludedCount: 0
};

export type ContactCommitteeMembersDialogProps = {
  open: boolean;
  committeeId: string | null;
  committeeName: string;
  inactiveParticipantCount: number;
  onClose(): void;
};

function emailCountCopy(count: number): string {
  return count === 1 ? "1 email address" : `${count} email addresses`;
}

function excludedCopy(count: number): string {
  return count === 1 ? "1 person has no usable email." : `${count} people have no usable email.`;
}

export default function ContactCommitteeMembersDialog({
  open,
  committeeId,
  committeeName,
  inactiveParticipantCount,
  onClose
}: ContactCommitteeMembersDialogProps) {
  const client = useApolloClient();
  const canViewPersonContact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonContact
  });
  const [field, setField] = useState<MailtoField>("bcc");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [assignmentNodes, setAssignmentNodes] = useState<CommitteeParticipantEmailNode[]>([]);
  const [selectedTitleIds, setSelectedTitleIds] = useState<Set<string>>(new Set());
  const [titlesSeeded, setTitlesSeeded] = useState(false);

  const {
    data: titlesData,
    loading: titlesLoading,
    error: titlesError
  } = useQuery(COMMITTEE_ASSIGNMENT_TITLES_QUERY, {
    skip: !open
  });
  const assignmentTitles: CommitteeAssignmentTitle[] = titlesData?.committeeAssignmentTitles ?? [];
  const assignmentTitleIdsKey = assignmentTitles.map((title) => title.id).join(",");

  const result = useMemo(() => {
    if (!titlesSeeded) return EMPTY_EMAIL_RESULT;
    if (assignmentTitleIdsKey && selectedTitleIds.size === 0) return EMPTY_EMAIL_RESULT;
    return collectEmailsFromAssignmentNodes(
      assignmentNodes,
      assignmentRoleFilter({
        catalogTitleIds: assignmentTitleIdsKey ? assignmentTitleIdsKey.split(",") : [],
        selectedTitleIds
      })
    );
  }, [assignmentNodes, assignmentTitleIdsKey, selectedTitleIds, titlesSeeded]);

  useEffect(() => {
    if (!open) {
      setTitlesSeeded(false);
      return;
    }
    setField("bcc");
    setIncludeInactive(false);
    setAssignmentNodes([]);
    setSelectedTitleIds(new Set());
    setTitlesSeeded(false);
  }, [open]);

  useEffect(() => {
    if (!open || !assignmentTitleIdsKey || titlesSeeded) return;
    setSelectedTitleIds(new Set(assignmentTitleIdsKey.split(",")));
    setTitlesSeeded(true);
  }, [assignmentTitleIdsKey, open, titlesSeeded]);

  useEffect(() => {
    if (open && !titlesLoading && !titlesError && assignmentTitles.length === 0 && !titlesSeeded) {
      setTitlesSeeded(true);
    }
  }, [assignmentTitles.length, open, titlesError, titlesLoading, titlesSeeded]);

  useEffect(() => {
    if (!open || !committeeId || !canViewPersonContact) return;

    let cancelled = false;
    setLoading(true);

    void fetchCommitteeParticipantEmails(client, { committeeId, includeInactive })
      .then((nextResult) => {
        if (cancelled) return;
        setAssignmentNodes(nextResult.nodes);
        if (nextResult.truncated) {
          showErrorSnackbar("Not all email addresses loaded", {
            subtitle: "You can still copy or open email with the addresses that loaded."
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setAssignmentNodes([]);
        showErrorSnackbar("Email addresses not loaded", { subtitle: "Please try again later." });
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, committeeId, includeInactive, canViewPersonContact, client]);

  const copyAddresses = useCallback(async () => {
    if (result.emails.length === 0) return false;

    try {
      await copyTextToClipboard(formatEmailAddressList(result.emails));
      return true;
    } catch {
      showErrorSnackbar("Email addresses not copied", { subtitle: "Please try again later." });
      return false;
    }
  }, [result.emails]);

  const handleCopyAddresses = useCallback(async () => {
    setCopying(true);
    try {
      const copied = await copyAddresses();
      if (copied) {
        showSnackbar("Email addresses copied");
      }
    } finally {
      setCopying(false);
    }
  }, [copyAddresses]);

  const handleOpenEmail = useCallback(async () => {
    if (result.emails.length === 0) return;

    const href = buildCommitteeMailtoHref({
      emails: result.emails,
      field,
      subject: committeeName
    });

    if (isMailtoTooLong(href)) {
      setCopying(true);
      try {
        const copied = await copyAddresses();
        if (copied) {
          showSnackbar("This list is too long to open in email. Addresses copied instead.");
        }
      } finally {
        setCopying(false);
      }
      return;
    }

    openMailtoHref(href);
  }, [committeeName, copyAddresses, field, result.emails]);

  const hasEmails = result.emails.length > 0;
  const areAllTitlesSelected =
    assignmentTitles.length > 0 && assignmentTitles.every((title) => selectedTitleIds.has(title.id));
  const actionsDisabled =
    loading ||
    copying ||
    titlesLoading ||
    Boolean(titlesError) ||
    !titlesSeeded ||
    (assignmentTitles.length > 0 && selectedTitleIds.size === 0) ||
    !hasEmails;

  const toggleTitle = (titleId: string) => {
    setSelectedTitleIds((previous) => {
      const next = new Set(previous);
      if (next.has(titleId)) {
        next.delete(titleId);
      } else {
        next.add(titleId);
      }
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      headline="Email Members"
      hasFormElements
      onClosed={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="email-committee-members-cancel" disabled={copying}>
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={() => void handleCopyAddresses()}
            disabled={actionsDisabled}
            testId="email-committee-members-copy"
          >
            Copy Addresses
          </Button>
          <Button
            variant="filled"
            onClick={() => void handleOpenEmail()}
            disabled={actionsDisabled}
            testId="email-committee-members-open"
          >
            Open Email
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6" data-testid="email-committee-members-dialog">
        <div className="flex flex-col gap-4">
          <Radio
            id="email-members-field-to"
            name="email-members-field"
            label="To"
            checked={field === "to"}
            onChange={(checked) => {
              if (checked) setField("to");
            }}
          />
          <Radio
            id="email-members-field-cc"
            name="email-members-field"
            label="Cc"
            checked={field === "cc"}
            onChange={(checked) => {
              if (checked) setField("cc");
            }}
          />
          <Radio
            id="email-members-field-bcc"
            name="email-members-field"
            label={<span id="email-members-field-bcc-label">Bcc</span>}
            aria-labelledby="email-members-field-bcc-label"
            supportingText="Hides addresses from other recipients."
            checked={field === "bcc"}
            onChange={(checked) => {
              if (checked) setField("bcc");
            }}
          />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <SubSectionHeading>Assignment Roles</SubSectionHeading>
            {assignmentTitles.length > 0 && (
              <Button
                variant="text"
                onClick={() =>
                  setSelectedTitleIds(
                    areAllTitlesSelected ? new Set() : new Set(assignmentTitles.map((title) => title.id))
                  )
                }
                testId="email-members-select-all-roles"
                ariaLabel={areAllTitlesSelected ? "Deselect all assignment roles" : "Select all assignment roles"}
              >
                {areAllTitlesSelected ? "Deselect all" : "Select all"}
              </Button>
            )}
          </div>
          {titlesError && (
            <p className="text-body-medium text-sys-brand-error" role="alert">
              Unable to load assignment roles. Try again later.
            </p>
          )}
          {assignmentTitles.map((title) => (
            <Checkbox
              key={title.id}
              name={`email-members-role-${title.id}`}
              label={title.title}
              checked={selectedTitleIds.has(title.id)}
              onChange={() => toggleTitle(title.id)}
            />
          ))}
        </div>
        <Checkbox
          name="include-inactive-members"
          label="Include Inactive Members"
          checked={includeInactive}
          disabled={inactiveParticipantCount === 0 || loading}
          onChange={setIncludeInactive}
        />
        {!titlesError && (
          <p className="text-body-medium" role="status">
            {loading || titlesLoading
              ? "Loading email addresses…"
              : hasEmails
                ? `${emailCountCopy(result.emails.length)}.${result.excludedCount > 0 ? ` ${excludedCopy(result.excludedCount)}` : ""}`
                : result.excludedCount > 0
                  ? `No email addresses. ${excludedCopy(result.excludedCount)}`
                  : "No email addresses."}
          </p>
        )}
      </div>
    </Dialog>
  );
}
